/**
 * useAdaptiveHome — Hook that bridges the pure suggestion engine with React
 * 
 * Reads real context from localStorage, checks for simulation override,
 * feeds the central engine, and returns the result.
 * Also handles session tracking and UX logging.
 */
import { useMemo, useEffect, useRef } from 'react';
import { getHomeSuggestionState } from '@/services/homeSuggestionEngine';
import { uxLog } from '@/services/homeUxLogger';
import { homeAnalytics } from '@/services/homeAnalytics';

// ─── localStorage keys ───
const KEYS = {
  FIRST_VISIT:       'hearen_first_visit_ts',
  SESSION_COUNT:     'hearen_session_count',
  LAST_THEME:        'hearen_last_theme_used',
  LAST_VARIANT_IDX:  'hearen_nudge_variant_idx',
  LAST_VARIANT_TS:   'hearen_nudge_variant_ts',
  AUDIOMETRIC:       'hearen_audiometric_results',
  TINNITUS:          'hearen_tinnitus_profile',
  SIM_ENABLED:       'hearen_sim_enabled',
  SIM_CONTEXT:       'hearen_sim_context',
};

// ─── Safe localStorage helpers ───
const safeGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const safeSet = (key, val) => { try { localStorage.setItem(key, val); } catch { /* noop */ } };

// ─── Time period ───
const getTimePeriod = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 11)  return 'matin';
  if (h >= 11 && h < 18) return 'journee';
  if (h >= 18 && h < 23) return 'soir';
  return 'nuit';
};

// ─── Build real user context ───
const buildRealContext = (user, isFr) => {
  const firstVisit = safeGet(KEYS.FIRST_VISIT);
  let daysSinceInstall = 0;
  if (firstVisit) {
    daysSinceInstall = Math.floor((Date.now() - parseInt(firstVisit, 10)) / (1000 * 60 * 60 * 24));
  }
  if (user?.created_at) {
    const created = new Date(user.created_at);
    if (!isNaN(created.getTime())) {
      daysSinceInstall = Math.max(daysSinceInstall, Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)));
    }
  }

  return {
    daysSinceInstall,
    sessionCount: parseInt(safeGet(KEYS.SESSION_COUNT) || '0', 10),
    lastThemeUsed: safeGet(KEYS.LAST_THEME) || null,
    hasCompletedPersonalization: !!(safeGet(KEYS.AUDIOMETRIC) && safeGet(KEYS.TINNITUS)),
    timeOfDay: getTimePeriod(),
    isFr,
    variantIndex: pickVariantIndex(),
  };
};

// ─── Variant rotation (6h cooldown) ───
const pickVariantIndex = () => {
  const lastIdx = parseInt(safeGet(KEYS.LAST_VARIANT_IDX) || '-1', 10);
  const lastTs = parseInt(safeGet(KEYS.LAST_VARIANT_TS) || '0', 10);
  const now = Date.now();
  const sixHours = 6 * 60 * 60 * 1000;

  if (lastIdx >= 0 && lastIdx < 4 && (now - lastTs) < sixHours) {
    return lastIdx;
  }

  let next = (lastIdx + 1) % 4;
  if (next === lastIdx && 4 > 1) next = (next + 1) % 4;

  safeSet(KEYS.LAST_VARIANT_IDX, next.toString());
  safeSet(KEYS.LAST_VARIANT_TS, now.toString());
  return next;
};

// ─── Read simulation context ───
const getSimulationContext = (isFr) => {
  if (safeGet(KEYS.SIM_ENABLED) !== 'true') return null;
  try {
    const raw = JSON.parse(safeGet(KEYS.SIM_CONTEXT) || 'null');
    if (!raw) return null;
    return { ...raw, isFr };
  } catch { return null; }
};

// ─── Track session start (called from HomeNew) ───
export const trackSessionStart = (themeId) => {
  const count = parseInt(safeGet(KEYS.SESSION_COUNT) || '0', 10);
  safeSet(KEYS.SESSION_COUNT, (count + 1).toString());
  if (themeId) safeSet(KEYS.LAST_THEME, themeId);
};

// ─── Main hook ───
const useAdaptiveHome = (user, isFr = true) => {
  const impressionSent = useRef(false);

  const result = useMemo(() => {
    // Ensure first visit is tracked
    if (!safeGet(KEYS.FIRST_VISIT)) {
      safeSet(KEYS.FIRST_VISIT, Date.now().toString());
    }

    const realContext = buildRealContext(user, isFr);
    const simContext = getSimulationContext(isFr);
    const isSimulation = !!simContext;

    const state = getHomeSuggestionState(realContext, simContext);

    // Log decision
    uxLog.decision(state, isSimulation);

    return {
      ...state,
      // Convenience aliases for HomeNew
      subtitle: state.homeSubtitle,
      nudge: state.visible ? {
        title: state.title,
        subtitle: state.subtitle,
        cta: state.cta,
        completed: state.completed,
        accent: state.accent,
      } : null,
      isSimulation,
      // Pass full state for analytics
      _fullState: state,
    };
  }, [user, isFr]);

  // Track impression once per mount when nudge is visible
  useEffect(() => {
    if (result.visible && !impressionSent.current) {
      impressionSent.current = true;
      uxLog.impression(result._fullState, result.isSimulation);
      homeAnalytics.impression(result._fullState, result.isSimulation);
    }
  }, [result]);

  return result;
};

export default useAdaptiveHome;
